/**
 * Issue #1530 — a transport blip on the FIRST message of a first session used
 * to lose her introduction to Kin, permanently and silently.
 *
 * ── What happened ─────────────────────────────────────────────────────────
 *
 * Production Sentry, Sep 8 2026 17:56 UTC: `[chat-persist] Failed to create
 * conversation: TypeError: Load failed`. The user had signed up at 17:51 and
 * signed in at 17:53 — she was three minutes into her first session. Supabase
 * had no incident that day, so this was a client-side transport failure, which
 * is unremarkable on mobile and especially likely right after an
 * email-confirmation hop: fresh app load, cold connections.
 *
 * The cost was out of all proportion to the blip. With no conversation row,
 * `convId` stays null, so BOTH the user message and the assistant reply are
 * skipped — the whole exchange. `conversationIdRef` also stays null, so the
 * next message creates a fresh conversation and everything after it persists
 * normally. Message 1 is the only unrecoverable one, and message 1 of a first
 * session is where she tells Kin about her family.
 *
 * ── Why postgrest-js does not retry this itself ───────────────────────────
 *
 * It deliberately won't: `RETRYABLE_METHODS = ['GET','HEAD','OPTIONS']`
 * (`@supabase/postgrest-js/src/types/common/common.ts`), and an insert is a
 * POST. `PostgrestBuilder.ts` rethrows the fetch rejection immediately, and the
 * outer catch converts it to `{ error, status: 0 }`.
 *
 * ── Why `status === 0` and not `isAuthNetworkError` ───────────────────────
 *
 * #1530's spec suggested reusing `isAuthNetworkError` from
 * `@/lib/authNetworkError`. It does not fit this shape — measured against a
 * real client with an unroutable host:
 *
 *     response keys : count, data, error, status, statusText, success
 *     status        : 0
 *     error.message : "TypeError: fetch failed"     ("Load failed" on WebKit)
 *     error.name    : undefined
 *     error.code    : ""
 *
 * `isAuthNetworkError` requires `name === "TypeError"`, and PostgREST's error
 * is a plain object with no `name` at all — the type name is embedded in the
 * message string instead. It would have returned false on every one of these.
 *
 * `status === 0` is the exact signal, set in precisely one place: the catch
 * that handles a rejected fetch. Every other outcome carries a real HTTP
 * status. It is also browser- and locale-independent, where the message
 * wording is neither.
 *
 * ── Why retrying a POST is acceptable here, stated honestly ───────────────
 *
 * It is not risk-free. `status === 0` means fetch REJECTED, which is *usually*
 * "the request never arrived" — but a connection dropped after the server
 * committed would also land here, and a retry then writes a second row. The
 * cost of that is one empty conversation in her history (the list in
 * `Index.tsx` filters only on `archived_at`, so an orphan with no messages
 * would show, and on Free it would occupy one of her five visible slots).
 *
 * Weighed against permanently losing the first exchange of a first session,
 * the trade is clearly worth it — but it is a trade, not a free win, and a
 * future change here should know that.
 *
 * ⚠️ A non-transport error (RLS, a constraint, anything with a real status)
 * is NOT retried. Retrying those burns time and hides the cause.
 */

/** PostgREST sets this status only when the fetch itself rejected. */
export const TRANSPORT_STATUS = 0;

/**
 * Two retries, ~1s of added delay in the worst case, and only ever on the
 * failure path. Kept short deliberately: this call sits in front of the
 * Anthropic request, so every millisecond here is a millisecond before Kin
 * starts replying.
 */
export const RETRY_DELAYS_MS = [250, 750];

/** The shape PostgREST returns for `.insert(...).select("id").single()`. */
export interface CreateAttemptResult {
  data: { id: string } | null;
  error: unknown;
  status: number;
}

/**
 * `PromiseLike`, not `Promise`: a PostgREST builder is a thenable and does not
 * carry `.catch`/`.finally`, so typing this as `Promise` rejects the very call
 * site it exists for.
 */
export type CreateAttempt = () => PromiseLike<CreateAttemptResult>;

export interface CreateOutcome {
  /** The new conversation id, or null if every attempt failed. */
  id: string | null;
  /** How many attempts ran — 1 when the first one worked. */
  attempts: number;
  /** The final error, for reporting. Null on success. */
  lastError: unknown;
  /** The final status, so the caller can tag network vs database. */
  lastStatus: number | null;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Runs `attempt`, retrying ONLY transport failures, at most `delays.length`
 * extra times.
 *
 * Takes a thunk rather than a Supabase client so the retry policy can be tested
 * without a client, and so this module stays free of PostgREST's generics.
 */
export async function createConversationWithRetry(
  attempt: CreateAttempt,
  options: { delays?: readonly number[]; sleep?: (ms: number) => Promise<void> } = {},
): Promise<CreateOutcome> {
  const delays = options.delays ?? RETRY_DELAYS_MS;
  const sleep = options.sleep ?? realSleep;

  let lastError: unknown = null;
  let lastStatus: number | null = null;
  let attempts = 0;

  for (let i = 0; i <= delays.length; i++) {
    const { data, error, status } = await attempt();
    attempts = i + 1;

    if (!error && data?.id) {
      return { id: data.id, attempts, lastError: null, lastStatus: status };
    }

    // A 2xx that somehow carried no id is not a transport problem and will not
    // heal by asking again. Reported rather than retried, with a real cause so
    // it cannot arrive as a bare "null".
    lastError = error ?? new Error(`conversation insert returned no id (status ${status})`);
    lastStatus = status;

    // Anything with a real HTTP status is a decision from the server, not a
    // dropped connection. Breaking here keeps the cause visible.
    if (status !== TRANSPORT_STATUS) break;
    if (i === delays.length) break;

    await sleep(delays[i]);
  }

  return { id: null, attempts, lastError, lastStatus };
}
